'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Container } from '@/atoms/Container/Container';
import { PostThreadConnector } from '@/atoms/PostThreadConnector/PostThreadConnector';
import { POST_THREAD_CONNECTOR_VARIANTS } from '@/atoms/PostThreadConnector/PostThreadConnector.constants';
import { Typography } from '@/atoms/Typography/Typography';
import { POST_MAX_CHARACTER_LENGTH } from '@/config/posts';
import { useCharacterLimitWarning } from '@/hooks/useCharacterLimitWarning/useCharacterLimitWarning';
import { useComposerHeightAnimation } from '@/hooks/useComposerHeightAnimation/useComposerHeightAnimation';
import { useEffectiveTagsLayout } from '@/hooks/useEffectiveTagsLayout/useEffectiveTagsLayout';
import { useElementHeight } from '@/hooks/useElementHeight/useElementHeight';
import { useEnterSubmit } from '@/hooks/useEnterSubmit/useEnterSubmit';
import { usePostInput } from '@/hooks/usePostInput/usePostInput';
import { usePostInputAuthHandlers } from '@/hooks/usePostInputAuthHandlers/usePostInputAuthHandlers';
import { canSubmitPost, cn, getEnforcedCharacterCount } from '@/libs/utils/utils';
import { POST_INPUT_VARIANT } from '@/organisms/PostInput/PostInput.constants';
import { selectDisplayUserPubky } from '@/stores/auth/auth.selectors';
import { useAuthStore } from '@/stores/auth/auth.store';
import { QUICK_REPLY_CONNECTOR_HEIGHT_OFFSET, QUICK_REPLY_PROMPTS } from './QuickReply.constants';
import type { QuickReplyContentProps, QuickReplyProps } from './QuickReply.types';
import { QuickReplyContent } from './QuickReplyContent';

export function QuickReply({
  parentPostId,
  connectorVariant = POST_THREAD_CONNECTOR_VARIANTS.LAST,
  onReplySubmitted,
}: QuickReplyProps) {
  const [promptIndex] = React.useState(() => Math.floor(Math.random() * QUICK_REPLY_PROMPTS.length));
  const prompt = QUICK_REPLY_PROMPTS[promptIndex] || QUICK_REPLY_PROMPTS[0];

  const {
    textareaRef,
    containerRef,
    fileInputRef,
    content,
    tags,
    attachments,
    setAttachments,
    isDragging,
    isExpanded,
    isSubmitting: isWriting,
    showEmojiPicker,
    setShowEmojiPicker,
    displayPlaceholder,
    currentUserPubky,
    currentUserDetails,
    handleExpand,
    handleSubmit,
    handleChange,
    handleEmojiSelect,
    handleFilesAdded,
    handleFileClick,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
    setTags,
    // Mention autocomplete
    mentionUsers,
    mentionIsOpen,
    mentionSelectedIndex,
    setMentionSelectedIndex,
    handleMentionSelect,
    handleMentionKeyDown,
    handleSelectionChange,
  } = usePostInput({
    variant: POST_INPUT_VARIANT.REPLY,
    postId: parentPostId,
    placeholder: prompt,
    expanded: false,
    onSuccess: onReplySubmitted,
  });

  const {
    isWaiting,
    isAuthenticated,
    handleExpandWithAuth,
    handleSubmitWithAuth,
    setTagsWithAuth,
    setAttachmentsWithAuth,
    handleChangeWithAuth,
    handleFilesAddedWithAuth,
    handleFileClickWithAuth,
    handleEmojiSelectWithAuth,
    handlePasteWithAuth,
    handleDragEventWithAuth,
    createKeyDownHandler,
  } = usePostInputAuthHandlers({
    handleExpand,
    handleSubmit,
    setTags,
    setAttachments,
    handleChange,
    handleFilesAdded,
    handleFileClick,
    handleEmojiSelect,
    handlePaste,
  });
  const isSubmitting = isWriting || isWaiting;
  const restoreStatus = useAuthStore((state) => state.restoreStatus);
  const displayUserPubky = selectDisplayUserPubky({ currentUserPubky, restoreStatus });

  const { ref: cardRef, height: cardHeight } = useElementHeight();
  const shouldReduceMotion = useReducedMotion();
  const { animatedHeight, heightTransition, heightTransitionStyle, onHeightAnimationComplete } =
    useComposerHeightAnimation({
      isExpanded,
      measuredHeight: cardHeight,
      shouldReduceMotion,
    });

  const isValid = () => canSubmitPost(POST_INPUT_VARIANT.REPLY, content, attachments, isSubmitting);

  const enterSubmitHandler = useEnterSubmit(isValid, handleSubmitWithAuth, {
    requireModifier: true,
  });

  // Combined keyboard handler: mention popover takes priority, then enter submit
  const handleKeyDown = createKeyDownHandler({ handleMentionKeyDown, enterSubmitHandler });

  // The measured height covers the composer content. Extend through the card
  // chrome and overlap its border by 1px at both ends for a seamless thread line.
  const connectorHeight = cardHeight ? cardHeight + QUICK_REPLY_CONNECTOR_HEIGHT_OFFSET : undefined;

  const effectiveTagsLayout = useEffectiveTagsLayout();
  const characterLimit = isExpanded
    ? { count: getEnforcedCharacterCount(content), max: POST_MAX_CHARACTER_LENGTH }
    : undefined;
  useCharacterLimitWarning(characterLimit);

  const contentProps: QuickReplyContentProps = {
    currentUserPubky: displayUserPubky,
    currentUserDetails,
    textareaRef,
    content,
    displayPlaceholder,
    isSubmitting,
    isAuthenticated,
    onChange: handleChangeWithAuth,
    onFocus: handleExpandWithAuth,
    onKeyDown: handleKeyDown,
    onSelect: handleSelectionChange,
    onPaste: handlePasteWithAuth,
    mentionIsOpen,
    mentionUsers,
    mentionSelectedIndex,
    onMentionSelect: handleMentionSelect,
    onMentionHover: setMentionSelectedIndex,
    fileInputRef,
    attachments,
    setAttachments: setAttachmentsWithAuth,
    onFilesAdded: handleFilesAddedWithAuth,
    isExpanded,
    tags,
    setTags: setTagsWithAuth,
    onSubmit: handleSubmitWithAuth,
    showEmojiPicker,
    setShowEmojiPicker,
    onEmojiSelect: handleEmojiSelectWithAuth,
    onImageClick: handleFileClickWithAuth,
    isPostDisabled: isAuthenticated ? !isValid() : false,
    characterLimit,
  };

  return (
    <Container
      overrideDefaults
      className="relative flex"
      data-testid="quick-reply"
      data-cy="quick-reply"
      aria-busy={isSubmitting}
    >
      <Container overrideDefaults className="relative w-3 shrink-0" data-testid="quick-reply-connector-column">
        <Container overrideDefaults className="absolute -inset-y-px left-0">
          <PostThreadConnector
            height={connectorHeight}
            variant={connectorVariant}
            style={heightTransitionStyle}
            data-testid="quick-reply-connector"
          />
        </Container>
      </Container>

      <Container
        ref={containerRef}
        data-state={isExpanded ? 'expanded' : 'collapsed'}
        className={cn(
          'relative w-full cursor-pointer rounded-md border border-dashed transition-colors duration-200',
          '[&_textarea::placeholder]:transition-opacity [&_textarea::placeholder]:duration-150',
          'focus-within:[&_textarea::placeholder]:opacity-0',
          'motion-reduce:[&_textarea::placeholder]:transition-none',
          'p-6',
          isDragging ? 'border-brand' : 'border-input',
        )}
        onClick={handleExpandWithAuth}
        onDragEnter={(event) => handleDragEventWithAuth(event, handleDragEnter)}
        onDragLeave={(event) => handleDragEventWithAuth(event, handleDragLeave)}
        onDragOver={(event) => handleDragEventWithAuth(event, handleDragOver)}
        onDrop={(event) => handleDragEventWithAuth(event, handleDrop)}
        overrideDefaults
      >
        {/* Drag overlay */}
        {isDragging && (
          <Container
            className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-brand/10"
            overrideDefaults
          >
            <Typography className="text-brand">Drop files here</Typography>
          </Container>
        )}

        <motion.div
          data-testid="quick-reply-state-height"
          className="overflow-hidden"
          initial={false}
          animate={{ height: animatedHeight }}
          transition={{ height: heightTransition }}
          onAnimationComplete={onHeightAnimationComplete}
        >
          <Container
            ref={cardRef}
            data-testid="quick-reply-state-content"
            className="relative flex min-w-0 flex-col gap-4"
            overrideDefaults
          >
            <QuickReplyContent {...contentProps} layout={effectiveTagsLayout} />
          </Container>
        </motion.div>
      </Container>
    </Container>
  );
}
